import React, { useState, useEffect, useCallback } from 'react';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  ComposedChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  Activity, 
  TrendingUp, 
  Clock, 
  RefreshCw, 
  Zap,
  Target,
  AlertCircle,
  BarChart3
} from 'lucide-react';

const IntradayChart = ({ symbol, currentPrice, quote }) => {
  const [intradayData, setIntradayData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [chartType, setChartType] = useState('line');
  const [timeframe, setTimeframe] = useState('1m');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [dayTradingStats, setDayTradingStats] = useState(null);
  const [actualTradingDate, setActualTradingDate] = useState(null);
  const [isShowingFallback, setIsShowingFallback] = useState(false);
  
  // Touch gesture support
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouchX, setLastTouchX] = useState(0);
  const [lastTouchY, setLastTouchY] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [panOffset, setPanOffset] = useState(0);
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [isPinching, setIsPinching] = useState(false);
  // Get most recent business day (trading day)
  const getMostRecentTradingDay = () => {
    const today = new Date();
    const day = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    
    // Clone date to avoid modifying the original
    const tradingDate = new Date(today);
    
    if (day === 0) { // Sunday
      tradingDate.setDate(tradingDate.getDate() - 2); // Previous Friday
    } else if (day === 6) { // Saturday  
      tradingDate.setDate(tradingDate.getDate() - 1); // Previous Friday
    }
    // For weekdays (Monday-Friday), show the current day
    // The backend will handle whether there's actual trading data available
    
    return tradingDate.toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState(getMostRecentTradingDay());

  // Check if we're showing previous day data
  const isShowingPreviousDay = () => {
    const today = new Date().toISOString().split('T')[0];
    return selectedDate !== today;
  };

  // Get day name for display
  const getDayName = (dateString) => {
    const date = new Date(dateString + 'T12:00:00'); // Add time to avoid timezone issues
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const fetchIntradayData = useCallback(async () => {
    if (!symbol) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? '/api' 
        : 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/intraday/${symbol}?interval=${timeframe}&date=${selectedDate}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch intraday data');
      }
      
      const data = await response.json();
      setIntradayData(data.timeSeries);
      setLastUpdate(new Date());
      calculateDayTradingStats(data.timeSeries);
      
      // Check if backend returned fallback data
      if (data.actualRequestedDate && data.tradingDate !== data.actualRequestedDate) {
        setActualTradingDate(data.tradingDate);
        setIsShowingFallback(true);
      } else {
        setActualTradingDate(null);
        setIsShowingFallback(false);
      }
      
      setError(null); // Clear any previous errors on successful fetch
    } catch (err) {
      setError(err.message);
      setIntradayData([]); // Clear old data on error
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe, selectedDate]);

  useEffect(() => {
    if (symbol) {
      fetchIntradayData();
    }
  }, [symbol, timeframe, selectedDate, fetchIntradayData]);

  useEffect(() => {
    // Only auto-refresh for current date
    const isToday = selectedDate === new Date().toISOString().split('T')[0];
    
    if (autoRefresh && symbol && isToday) {
      const interval = setInterval(() => {
        fetchIntradayData();
      }, 30000); // Update every 30 seconds for Yahoo Finance
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh, symbol, selectedDate, fetchIntradayData]);

  const calculateDayTradingStats = (data) => {
    if (!data || data.length === 0) return;

    const prices = data.map(d => d.close);
    const volumes = data.map(d => d.volume);
    
    const dayHigh = Math.max(...prices);
    const dayLow = Math.min(...prices);
    const dayOpen = data[0].open;
    const currentClose = prices[prices.length - 1];
    
    const dayRange = dayHigh - dayLow;
    const dayChange = currentClose - dayOpen;
    const dayChangePercent = ((dayChange / dayOpen) * 100).toFixed(2);
    
    // Support and resistance levels (simplified)
    const supportLevel = calculateSupportLevel(prices);
    const resistanceLevel = calculateResistanceLevel(prices);
    
    // Average volume
    const avgVolume = volumes.reduce((a, b) => a + b, 0) / volumes.length;
    
    // Volatility (last 30 data points)
    const recentPrices = prices.slice(-30);
    const volatility = calculateIntraVolatility(recentPrices);
    
    setDayTradingStats({
      dayHigh,
      dayLow,
      dayOpen,
      dayRange,
      dayChange,
      dayChangePercent,
      supportLevel,
      resistanceLevel,
      avgVolume,
      volatility,
      totalVolume: volumes.reduce((a, b) => a + b, 0)
    });
  };

  const calculateSupportLevel = (prices) => {
    // Simple support calculation - find local lows
    const sortedPrices = [...prices].sort((a, b) => a - b);
    return sortedPrices[Math.floor(sortedPrices.length * 0.2)];
  };

  const calculateResistanceLevel = (prices) => {
    // Simple resistance calculation - find local highs
    const sortedPrices = [...prices].sort((a, b) => b - a);
    return sortedPrices[Math.floor(sortedPrices.length * 0.2)];
  };

  const calculateIntraVolatility = (prices) => {
    if (prices.length < 2) return 0;
    
    const returns = [];
    for (let i = 1; i < prices.length; i++) {
      returns.push((prices[i] - prices[i-1]) / prices[i-1]);
    }
    
    const mean = returns.reduce((a, b) => a + b) / returns.length;
    const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / returns.length;
    return Math.sqrt(variance) * 100; // Convert to percentage
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
  };

  const formatTooltipTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="intraday-tooltip">
          <p className="tooltip-time">{formatTooltipTime(label)}</p>
          <div className="tooltip-data">
            <p>Open: <span className="tooltip-value">${data.open.toFixed(2)}</span></p>
            <p>High: <span className="tooltip-value tooltip-high">${data.high.toFixed(2)}</span></p>
            <p>Low: <span className="tooltip-value tooltip-low">${data.low.toFixed(2)}</span></p>
            <p>Close: <span className="tooltip-value">${data.close.toFixed(2)}</span></p>
            <p>Volume: <span className="tooltip-value">{data.volume.toLocaleString()}</span></p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading && intradayData.length === 0) {
    return (
      <div className="card loading-skeleton">
        <div className="intraday-header">
          <div className="skeleton-item" style={{height: '1.5rem', width: '40%'}}></div>
          <div className="skeleton-item" style={{height: '1rem', width: '30%'}}></div>
        </div>
        <div className="skeleton-item" style={{height: '20rem', marginBottom: '1rem'}}></div>
        <div className="skeleton-item" style={{height: '3rem'}}></div>
      </div>
    );
  }

  // Don't return early on error - show full component with controls

  const hasData = intradayData && intradayData.length > 0;

  // Intelligently sample data based on timeframe and data size
  const getOptimizedData = () => {
    if (!intradayData || intradayData.length === 0) return [];
    
    const dataLength = intradayData.length;
    
    // For smaller datasets or longer timeframes, show all data
    if (dataLength <= 200 || timeframe === '30m' || timeframe === '15m') {
      return intradayData;
    }
    
    // For large datasets with short timeframes (1m, 5m), intelligently sample
    if (timeframe === '1m' && dataLength > 500) {
      // Show every 2nd data point to maintain chart readability
      return intradayData.filter((_, index) => index % 2 === 0);
    } else if (timeframe === '5m' && dataLength > 300) {
      // Show every other data point for 5m timeframe if too dense
      return intradayData.filter((_, index) => index % 2 === 0);
    }
    
    // Default: show all data
    return intradayData;
  };

  // Helper function to calculate distance between two touches
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Touch gesture handlers for mobile support
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setIsPinching(false);
      setLastTouchX(touch.clientX);
      setLastTouchY(touch.clientY);
    } else if (e.touches.length === 2) {
      // Pinch gesture
      setIsPinching(true);
      setIsDragging(false);
      const distance = getTouchDistance(e.touches);
      setInitialPinchDistance(distance);
    }
  };

  const handleTouchMove = (e) => {
    // Only prevent default if we're actually handling the gesture
    if ((isPinching && e.touches.length === 2) || (isDragging && e.touches.length === 1)) {
      // Try to prevent default, but don't break if it's a passive listener
      try {
        e.preventDefault();
      } catch (error) {
        // Passive event listener - preventDefault not allowed, continue without it
      }
    }
    
    if (isPinching && e.touches.length === 2) {
      // Handle pinch-to-zoom
      const distance = getTouchDistance(e.touches);
      const scale = distance / initialPinchDistance;
      
      // Apply zoom based on pinch scale
      const zoomChange = (scale - 1) * 50; // Sensitivity adjustment
      setZoomLevel(prev => Math.max(20, Math.min(200, prev + zoomChange)));
      setInitialPinchDistance(distance);
    } else if (isDragging && e.touches.length === 1) {
      // Handle single-finger pan/zoom
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastTouchX;
      const deltaY = touch.clientY - lastTouchY;
      
      // For touch, prioritize the larger movement direction
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        // Vertical movement - zoom in/out
        const zoomSensitivity = 0.3;
        const zoomChange = -deltaY * zoomSensitivity; // Negative to make up = zoom in
        setZoomLevel(prev => Math.max(20, Math.min(200, prev + zoomChange)));
      } else {
        // Horizontal movement - pan through time
        const panSensitivity = 0.5;
        const panChange = -deltaX * panSensitivity; // Negative to make right = go forward in time
        const totalPoints = intradayData?.length || 0;
        const pointsToShow = Math.max(10, Math.floor((totalPoints * zoomLevel) / 100));
        const maxPanOffset = Math.max(0, totalPoints - pointsToShow);
        
        setPanOffset(prev => Math.max(0, Math.min(maxPanOffset, prev + panChange)));
      }
      
      setLastTouchX(touch.clientX);
      setLastTouchY(touch.clientY);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsPinching(false);
  };

  // Mouse wheel support for desktop
  const handleWheel = (e) => {
    // Only prevent default for zoom interactions, not regular scrolling
    try {
      e.preventDefault();
    } catch (error) {
      // Passive event listener - preventDefault not allowed, continue without it
    }
    
    const zoomSensitivity = 0.1;
    const zoomChange = e.deltaY * zoomSensitivity;
    setZoomLevel(prev => Math.max(20, Math.min(200, prev - zoomChange)));
  };

  // Get filtered data based on zoom and pan
  const getFilteredData = () => {
    const chartData = getOptimizedData();
    if (!chartData || chartData.length === 0) return [];
    
    const totalPoints = chartData.length;
    const pointsToShow = Math.max(10, Math.floor((totalPoints * zoomLevel) / 100));
    
    // If showing all data or more than available, return all
    if (pointsToShow >= totalPoints) {
      return chartData;
    }
    
    // Apply pan offset to determine which slice of data to show
    const maxOffset = totalPoints - pointsToShow;
    const clampedOffset = Math.max(0, Math.min(maxOffset, panOffset));
    const startIndex = Math.max(0, totalPoints - pointsToShow - clampedOffset);
    const endIndex = startIndex + pointsToShow;
    
    return chartData.slice(startIndex, endIndex);
  };

  const renderChart = () => {
    const chartData = getFilteredData();
    
    switch (chartType) {
      case 'area':
        return (
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="intradayGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatTime}
              domain={['dataMin', 'dataMax']}
            />
            <YAxis 
              domain={['dataMin - 0.5', 'dataMax + 0.5']}
              tickFormatter={(value) => `$${value.toFixed(2)}`}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            {dayTradingStats && (
              <>
                <ReferenceLine 
                  y={dayTradingStats.supportLevel} 
                  stroke="#ef4444" 
                  strokeDasharray="5 5" 
                  label="Support"
                />
                <ReferenceLine 
                  y={dayTradingStats.resistanceLevel} 
                  stroke="#3b82f6" 
                  strokeDasharray="5 5" 
                  label="Resistance"
                />
              </>
            )}
            <Area 
              type="monotone" 
              dataKey="close" 
              stroke="#10b981" 
              fillOpacity={1} 
              fill="url(#intradayGradient)"
            />
          </AreaChart>
        );

      case 'candlestick':
        return (
          <ComposedChart data={chartData}>
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatTime}
              domain={['dataMin', 'dataMax']}
            />
            <YAxis 
              domain={['dataMin - 0.5', 'dataMax + 0.5']}
              tickFormatter={(value) => `$${value.toFixed(2)}`}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            {dayTradingStats && (
              <>
                <ReferenceLine 
                  y={dayTradingStats.supportLevel} 
                  stroke="#ef4444" 
                  strokeDasharray="5 5" 
                  label="Support"
                />
                <ReferenceLine 
                  y={dayTradingStats.resistanceLevel} 
                  stroke="#3b82f6" 
                  strokeDasharray="5 5" 
                  label="Resistance"
                />
              </>
            )}
            {/* High-Low lines */}
            <Line type="monotone" dataKey="high" stroke="transparent" strokeWidth={0} dot={false} />
            <Line type="monotone" dataKey="low" stroke="transparent" strokeWidth={0} dot={false} />
            {/* Open-Close bars */}
            <Bar 
              dataKey="open" 
              fill="transparent" 
              stroke="transparent"
              shape={({ payload, x, y, width, height }) => {
                if (!payload) return null;
                const { open, high, low, close } = payload;
                const isPositive = close >= open;
                const candleWidth = Math.max(width * 0.7, 3);
                const centerX = x + width / 2;
                
                // Calculate Y positions based on the chart's scale
                if (chartData.length === 0) return null;
                const minVal = Math.min(...chartData.map(d => Math.min(d.low, d.high, d.open, d.close)));
                const maxVal = Math.max(...chartData.map(d => Math.max(d.low, d.high, d.open, d.close)));
                const range = maxVal - minVal || 1;
                
                const getY = (value) => y + height - ((value - minVal) / range) * height;
                
                const highY = getY(high);
                const lowY = getY(low);
                const openY = getY(open);
                const closeY = getY(close);
                const bodyTop = Math.min(openY, closeY);
                const bodyBottom = Math.max(openY, closeY);
                const bodyHeight = Math.max(bodyBottom - bodyTop, 1);
                
                const color = isPositive ? '#10b981' : '#ef4444';
                
                return (
                  <g>
                    {/* High-Low wick */}
                    <line
                      x1={centerX}
                      y1={highY}
                      x2={centerX}
                      y2={lowY}
                      stroke={color}
                      strokeWidth={1}
                    />
                    {/* Open-Close body */}
                    <rect
                      x={centerX - candleWidth / 2}
                      y={bodyTop}
                      width={candleWidth}
                      height={bodyHeight}
                      fill={isPositive ? 'white' : color}
                      stroke={color}
                      strokeWidth={1.5}
                    />
                  </g>
                );
              }}
            />
          </ComposedChart>
        );
      
      default:
        return (
          <LineChart data={chartData}>
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatTime}
              domain={['dataMin', 'dataMax']}
            />
            <YAxis 
              domain={['dataMin - 0.5', 'dataMax + 0.5']}
              tickFormatter={(value) => `$${value.toFixed(2)}`}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            {dayTradingStats && (
              <>
                <ReferenceLine 
                  y={dayTradingStats.supportLevel} 
                  stroke="#ef4444" 
                  strokeDasharray="5 5" 
                  label="Support"
                />
                <ReferenceLine 
                  y={dayTradingStats.resistanceLevel} 
                  stroke="#3b82f6" 
                  strokeDasharray="5 5" 
                  label="Resistance"
                />
              </>
            )}
            <Line 
              type="monotone" 
              dataKey="close" 
              stroke="#10b981" 
              strokeWidth={2} 
              dot={false}
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="card intraday-chart">
      <div className="intraday-header">
        <div className="intraday-title-section">
          <h3 className="card-title">
            <Zap size={20} />
            Day Trading Chart - {symbol}
          </h3>
          <div className="intraday-badge">
            Yahoo Finance
          </div>
        </div>
        
        <div className="intraday-controls">
          <div className="timeframe-controls">
            <button
              onClick={() => setTimeframe('1m')}
              className={`timeframe-btn ${timeframe === '1m' ? 'active' : ''}`}
            >
              1m
            </button>
            <button
              onClick={() => setTimeframe('5m')}
              className={`timeframe-btn ${timeframe === '5m' ? 'active' : ''}`}
            >
              5m
            </button>
            <button
              onClick={() => setTimeframe('15m')}
              className={`timeframe-btn ${timeframe === '15m' ? 'active' : ''}`}
            >
              15m
            </button>
            <button
              onClick={() => setTimeframe('30m')}
              className={`timeframe-btn ${timeframe === '30m' ? 'active' : ''}`}
            >
              30m
            </button>
          </div>
          
          <div className="date-picker-controls">
            <label htmlFor="trading-date" className="date-label">
              <Clock size={16} />
              Date:
            </label>
            <input
              id="trading-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]} // Can't select future dates
              className="date-picker"
            />
            {(isShowingPreviousDay() || isShowingFallback) && (
              <div style={{
                fontSize: '0.75rem',
                color: '#f59e0b',
                fontWeight: '500',
                marginTop: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <AlertCircle size={12} />
                {isShowingFallback 
                  ? `Showing ${getDayName(actualTradingDate)} (no data for selected date)`
                  : `Previous business day (${getDayName(selectedDate)})`
                }
              </div>
            )}
          </div>
          
          <div className="chart-type-controls">
            <button
              onClick={() => setChartType('line')}
              className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
            >
              <Activity size={16} />
              Line
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`chart-type-btn ${chartType === 'area' ? 'active' : ''}`}
            >
              <TrendingUp size={16} />
              Area
            </button>
            <button
              onClick={() => setChartType('candlestick')}
              className={`chart-type-btn ${chartType === 'candlestick' ? 'active' : ''}`}
            >
              <BarChart3 size={16} />
              Candle
            </button>
          </div>
          
          <div className="refresh-controls">
            {isShowingPreviousDay() && (
              <button
                onClick={() => setSelectedDate(getMostRecentTradingDay())}
                className="auto-refresh-btn"
                title="Go to current trading day"
              >
                <TrendingUp size={16} />
                Today
              </button>
            )}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`auto-refresh-btn ${autoRefresh ? 'active' : ''}`}
            >
              <Clock size={16} />
              {autoRefresh ? 'Auto' : 'Manual'}
            </button>
            <button
              onClick={fetchIntradayData}
              className="manual-refresh-btn"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Day Trading Stats */}
      {hasData && dayTradingStats && (
        <div className="day-trading-stats">
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Day Range</span>
              <span className="stat-value">
                ${dayTradingStats.dayLow.toFixed(2)} - ${dayTradingStats.dayHigh.toFixed(2)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Day Change</span>
              <span className={`stat-value ${dayTradingStats.dayChange >= 0 ? 'positive' : 'negative'}`}>
                {dayTradingStats.dayChange >= 0 ? '+' : ''}${dayTradingStats.dayChange.toFixed(2)} 
                ({dayTradingStats.dayChangePercent}%)
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Support</span>
              <span className="stat-value support">${dayTradingStats.supportLevel.toFixed(2)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Resistance</span>
              <span className="stat-value resistance">${dayTradingStats.resistanceLevel.toFixed(2)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Volatility</span>
              <span className="stat-value">{dayTradingStats.volatility.toFixed(2)}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Volume</span>
              <span className="stat-value">{dayTradingStats.totalVolume.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      {hasData ? (
        <div 
          className="intraday-chart-container"
          style={{ 
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            position: 'relative'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          <ResponsiveContainer width="100%" height={400}>
            {renderChart()}
          </ResponsiveContainer>
          
          {/* Touch gesture hint for mobile */}
          {zoomLevel !== 100 || panOffset > 0 ? (
            <div style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              pointerEvents: 'none'
            }}>
              Zoom: {Math.round(zoomLevel)}%
              {panOffset > 0 && ` • Offset: ${Math.round(panOffset)}`}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="no-data-state">
          <div className="no-data-content">
            {error ? (
              <>
                <AlertCircle className="no-data-icon" style={{color: '#ef4444'}} />
                <h4>Error Loading Intraday Data</h4>
                <p>{error}</p>
              </>
            ) : (
              <>
                <Zap className="no-data-icon" />
                <h4>No Intraday Data Available</h4>
                <p>
                  {!symbol 
                    ? "Select a stock to view intraday trading data"
                    : `No intraday data found for ${symbol} on ${selectedDate}. Try selecting a different trading day.`
                  }
                </p>
              </>
            )}
            {symbol && (
              <button onClick={fetchIntradayData} className="retry-button" disabled={loading}>
                <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                {loading ? 'Loading...' : (error ? 'Retry' : 'Refresh')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Chart Info */}
      {hasData && (
        <div className="intraday-chart-info">
        <div className="info-left">
          <Target size={14} />
          <span>Showing {getOptimizedData().length} data points ({timeframe} intervals)</span>
        </div>
        <div className="info-right">
          {lastUpdate && (
            <span>Updated: {lastUpdate.toLocaleTimeString()}</span>
          )}
        </div>
        <div className="intraday-disclaimer">
          <AlertCircle size={14} />
          <span>
            Intraday data from Yahoo Finance for {selectedDate}. 
            {hasData && (
              <>
                Showing {getOptimizedData().length} of {intradayData.length} data points
                {getOptimizedData().length < intradayData.length && ' (optimized for performance)'}. 
              </>
            )}
            {selectedDate === new Date().toISOString().split('T')[0] 
              ? 'Auto-refresh enabled (30 seconds).' 
              : 'Historical data - auto-refresh disabled.'
            }
          </span>
        </div>
      </div>
      )}
    </div>
  );
};

export default IntradayChart;